import { useQuery } from '@apollo/client';
import {
    faArrowUpRightFromSquare,
    faCheck,
    faChevronRight,
    faTriangleExclamation,
    faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { format, subDays } from 'date-fns';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { withIcon } from '../../core/components/Icon.tsx';
import { RowControlIconButton } from '../../core/components/Table.tsx';
import classnames from '../../core/utils/classnames.tsx';
import { isoDateFormat } from '../../core/utils/dates.ts';
import { url } from '../../core/utils/link';
import { getDateLocalizations } from '../../core/utils/localization.ts';
import { DocumentType, FIELD_ASSISTANCE_PATHS, mapDocumentTypeToGqlDocumentType } from '../constants.ts';
import { GROUP_SEPARATOR } from '../pages/Assistance/useDocumentConfig';
import { GET_FIELD_ASSISTANCE_EXAMPLES, GET_FIELD_ASSISTANCE_STATS } from '../queries.ts';
import FieldsConfig from './FieldsConfig.tsx';

const WarningIcon = withIcon(faTriangleExclamation);
const ChevronIcon = withIcon(faChevronRight);
const OpenIcon = withIcon(faArrowUpRightFromSquare);
const CorrectIcon = withIcon(faCheck);
const CorrectedIcon = withIcon(faXmark);

const PERIOD_DAYS = 7;
// the backend returns at most 50 documents per request
const MAX_MORE_DOCUMENTS = 50;
const REASONS = ['FIELD_UNCONFIDENT', 'FIELD_INVALID', 'VALIDATION_CHECK_FAILED', 'ORDER_MATCHING_FAILED'];

interface ReasonStat {
    reason: string;
    documentCount: number;
}

interface FieldStat {
    group: string;
    fieldName: string;
    documentCount: number;
    reasons: ReasonStat[];
}

interface ExampleValue {
    subField: string | null;
    lineItemIndex: number | null;
    readValue: string | null;
    correctedValue: string | null;
}

interface Example {
    recordId: string;
    finishedAt: string;
    isCorrected: boolean;
    documentNumber: string | null;
    clientName: string | null;
    clientNumber: string | null;
    values: ExampleValue[];
}

interface StatsVariables {
    documentType: string;
    channelId: string;
    startDate: string;
    endDate: string;
}

interface FieldAssistance {
    title: string;
    banner: React.ReactNode;
    details: React.ReactNode;
}

interface DetailsContext {
    variables: StatsVariables;
    assistancePath: string;
    getSubfieldLabel: (subField: string) => string;
}

const FieldAssistanceBanner = ({ onClick = undefined, children }: { onClick?: () => void; children: string }) => {
    const className = 'flex w-full items-center gap-2 rounded-lg px-4 py-3 text-left text-sm';
    const content = (
        <>
            <WarningIcon className="flex-none text-content-warning-strong" />
            <span className="flex-1 font-medium text-content-warning-strong">{children}</span>
        </>
    );

    if (!onClick) {
        return <div className={classnames(className, 'bg-surface-warning-weak')}>{content}</div>;
    }
    return (
        <button
            type="button"
            onClick={onClick}
            className={classnames(
                className,
                'cursor-pointer bg-surface-warning-weak-default transition-colors hover:bg-surface-warning-weak-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-brand-strong'
            )}
        >
            {content}
            <ChevronIcon className="flex-none text-content-warning-strong" />
        </button>
    );
};

const ExampleValueLine = ({
    value,
    isCorrected,
    getSubfieldLabel,
}: {
    value: ExampleValue;
    isCorrected: boolean;
    getSubfieldLabel: (subField: string) => string;
}) => {
    const { t } = useTranslation('config');
    const textOf = (text: string | null) => text || t('configPage.document.assistance.empty');
    const context = [
        value.subField && getSubfieldLabel(value.subField),
        value.lineItemIndex != null &&
            t('configPage.document.assistance.lineItem', { number: value.lineItemIndex + 1 }),
    ].filter(Boolean);

    let text: React.ReactNode;
    if (!isCorrected) {
        text = <span className="text-content-strong">{textOf(value.readValue ?? value.correctedValue)}</span>;
    } else if (value.readValue == null) {
        // the backend only stores the whole field's text as read, so a subfield has no read value
        text = (
            <span className="text-content-strong">
                {t('configPage.document.assistance.correctedTo', { value: textOf(value.correctedValue) })}
            </span>
        );
    } else {
        text = (
            <>
                <span className={classnames('text-content-medium', value.readValue && 'line-through')}>
                    {textOf(value.readValue)}
                </span>
                <span className="text-content-medium"> → </span>
                <span className="text-content-strong">{textOf(value.correctedValue)}</span>
            </>
        );
    }

    return (
        <span className="flex flex-col gap-0.5">
            {context.length > 0 && <span className="text-xs text-content-medium">{context.join(' · ')}</span>}
            <span className="break-words">{text}</span>
        </span>
    );
};

const OutcomeCircle = ({ isCorrected }: { isCorrected: boolean }) => {
    const { t } = useTranslation('config');
    const label = t(
        isCorrected ? 'configPage.document.assistance.corrected' : 'configPage.document.assistance.extractedCorrectly'
    );
    const OutcomeIcon = isCorrected ? CorrectedIcon : CorrectIcon;

    return (
        <span
            role="img"
            aria-label={label}
            title={label}
            className={classnames(
                '-mr-3 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full text-[11px]',
                isCorrected
                    ? 'bg-surface-danger-weak text-content-danger-strong'
                    : 'bg-surface-success-weak text-content-success-strong'
            )}
        >
            <OutcomeIcon />
        </span>
    );
};

const ExampleRow = ({ example, context }: { example: Example; context: DetailsContext }) => {
    const { t } = useTranslation('config');
    const date = format(new Date(example.finishedAt), 'd MMM', { locale: getDateLocalizations() });
    const lead = example.clientName ?? example.documentNumber ?? date;
    const details = example.clientName
        ? [example.clientNumber, date].filter(Boolean).join(' · ')
        : example.documentNumber && date;
    const openLabel = t('configPage.document.assistance.openDocument', {
        document: example.documentNumber ?? date,
    });

    return (
        <div className="flex items-start gap-4 px-4 py-3 text-sm">
            <span className="flex w-40 flex-none flex-col gap-0.5" title={lead}>
                <span className="truncate font-medium text-content-strong">{lead}</span>
                {details && <span className="truncate text-xs text-content-medium">{details}</span>}
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-2">
                {example.values.length > 0 ? (
                    example.values.map((value, index) => (
                        <ExampleValueLine
                            key={index}
                            value={value}
                            isCorrected={example.isCorrected}
                            getSubfieldLabel={context.getSubfieldLabel}
                        />
                    ))
                ) : (
                    <span className="text-content-medium">
                        {t(
                            example.isCorrected
                                ? 'configPage.document.assistance.corrected'
                                : 'configPage.document.assistance.extractedCorrectly'
                        )}
                    </span>
                )}
            </span>
            <OutcomeCircle isCorrected={example.isCorrected} />
            <RowControlIconButton className="-my-1.5 flex-none" asChild>
                <Link
                    to={url(context.assistancePath, { recordId: example.recordId })}
                    target="_blank"
                    aria-label={openLabel}
                    title={openLabel}
                >
                    <OpenIcon />
                </Link>
            </RowControlIconButton>
        </div>
    );
};

interface ReasonExamplesProps {
    stat: FieldStat;
    reason: ReasonStat;
    context: DetailsContext;
}

const ReasonExamples = ({ stat, reason, context }: ReasonExamplesProps) => {
    const { t } = useTranslation('config');
    const [isOpen, setOpen] = useState(false);
    const variables = { ...context.variables, group: stat.group, fieldName: stat.fieldName, reason: reason.reason };
    const { data: latest } = useQuery(GET_FIELD_ASSISTANCE_EXAMPLES, { variables: { ...variables, first: 1 } });
    const { data: others } = useQuery(GET_FIELD_ASSISTANCE_EXAMPLES, {
        variables: { ...variables, offset: 1, first: MAX_MORE_DOCUMENTS },
        skip: !isOpen,
    });
    const examples: Example[] = [
        ...(latest?.fieldAssistanceExamples ?? []),
        ...(isOpen ? (others?.fieldAssistanceExamples ?? []) : []),
    ];
    const moreCount = Math.min(reason.documentCount - 1, MAX_MORE_DOCUMENTS);
    const reasonKey = REASONS.includes(reason.reason) ? reason.reason : 'other';

    return (
        <FieldsConfig.Section
            title={t(`configPage.document.assistance.reasons.${reasonKey}.title`)}
            description={t(`configPage.document.assistance.reasons.${reasonKey}.help`)}
        >
            <FieldsConfig.Card
                footer={
                    moreCount > 0 && (
                        <FieldsConfig.MoreRow
                            isOpen={isOpen}
                            label={t('configPage.document.assistance.moreDocuments', { count: moreCount })}
                            onClick={() => setOpen(!isOpen)}
                        />
                    )
                }
            >
                {examples.map((example) => (
                    <ExampleRow key={example.recordId} example={example} context={context} />
                ))}
            </FieldsConfig.Card>
        </FieldsConfig.Section>
    );
};

interface FieldAssistanceDetailsProps {
    stat: FieldStat;
    documentCount: number;
    context: DetailsContext;
}

const FieldAssistanceDetails = ({ stat, documentCount, context }: FieldAssistanceDetailsProps) => {
    const { t } = useTranslation('config');
    return (
        <>
            <FieldAssistanceBanner>
                {t('configPage.document.assistance.summary', {
                    count: documentCount,
                    affected: stat.documentCount,
                    days: PERIOD_DAYS,
                })}
            </FieldAssistanceBanner>
            {stat.reasons.map((reason) => (
                <ReasonExamples key={reason.reason} stat={stat} reason={reason} context={context} />
            ))}
        </>
    );
};

interface UseFieldAssistanceProps {
    documentType: DocumentType;
    channelId: string;
    group: 'header' | 'line_item';
    getLabel: (fieldName: string) => string;
}

export const useFieldAssistance = ({ documentType, channelId, group, getLabel }: UseFieldAssistanceProps) => {
    const { t } = useTranslation('config');
    const assistancePath = FIELD_ASSISTANCE_PATHS[documentType];
    // computed once: new dates on every render would change the variables and refetch endlessly
    const [period] = useState(() => {
        const today = new Date();
        return {
            startDate: format(subDays(today, PERIOD_DAYS - 1), isoDateFormat),
            endDate: format(today, isoDateFormat),
        };
    });
    const variables: StatsVariables = {
        documentType: mapDocumentTypeToGqlDocumentType[documentType],
        channelId,
        ...period,
    };
    const { data } = useQuery(GET_FIELD_ASSISTANCE_STATS, { variables, skip: !channelId || !assistancePath });
    const stats = data?.fieldAssistanceStats;

    const statOf = (fieldName: string): FieldStat | undefined =>
        stats?.fields.find((field: FieldStat) => field.group === group && field.fieldName === fieldName);

    const withAssistanceCounts = <T extends { name: string }>(navFields: T[]) =>
        navFields.map((navField) => {
            const count = statOf(navField.name)?.documentCount ?? 0;
            return {
                ...navField,
                assistanceCount: count,
                assistanceHint: count
                    ? t('configPage.document.assistance.fieldHint', { count, days: PERIOD_DAYS })
                    : undefined,
            };
        });

    // why documents needed assistance because of the field: a banner on its page that opens the details
    const assistanceOf = (fieldName: string, onOpen: () => void): FieldAssistance | undefined => {
        const stat = statOf(fieldName);
        if (!stat) return undefined;

        const context: DetailsContext = {
            variables,
            assistancePath,
            getSubfieldLabel: (subField) => getLabel(`${fieldName}${GROUP_SEPARATOR}${subField}`),
        };
        return {
            title: t('configPage.document.assistance.title'),
            banner: (
                <FieldAssistanceBanner onClick={onOpen}>
                    {t('configPage.document.assistance.banner', {
                        count: stat.documentCount,
                        field: getLabel(fieldName),
                        days: PERIOD_DAYS,
                    })}
                </FieldAssistanceBanner>
            ),
            details: <FieldAssistanceDetails stat={stat} documentCount={stats.documentCount} context={context} />,
        };
    };

    return { withAssistanceCounts, assistanceOf };
};
